/**
 * §A.1 · Lo que se le puede hacer a un club desde aquí.
 *
 * Tres acciones, y las tres cambian algo de verdad, así que las tres se confirman.
 *
 * ## «Suspender» no es «apagar», y hay que decirlo donde se pulsa
 *
 * `academies.status = 'suspended'` deja **las lecturas vivas** y devuelve `423` en
 * las escrituras. El contrato explica por qué: «un club que se retrasa un mes en
 * pagarnos no puede dejar a 120 familias sin ver el historial de sus hijos: eso nos
 * hunde la reputación mucho más de lo que vale la cuota».
 *
 * Quien pulsa este botón está enfadado con un club que no paga, y va a creer que lo
 * está apagando. Si no se lo decimos aquí, lo va a pulsar creyendo una cosa y
 * haciendo otra —y peor: no lo va a pulsar, por miedo a apagar a 120 familias—. Por
 * eso el texto de la confirmación es la explicación y no un «¿estás seguro?».
 *
 * ## Un club en `prospect` no se activa
 *
 * Responde `409 academy_has_no_owner`: es una ficha del censo federativo, sin nadie
 * detrás. El botón se enseña igual y el error se pinta tal cual, porque el `detail`
 * del servidor lo explica mejor de lo que podría explicarlo un botón gris. Y el
 * estado `prospect` no está en `EstadoDeClub`, así que no se puede comprobar aquí
 * sin inventárselo.
 *
 * ## El arranque rompe el arranque en frío de dos lados
 *
 * `brainstorming.md` §12: si el club no usa la app las familias no la instalan, y si
 * las familias no la tienen el club deja de usarla. `POST /onboarding` manda el aviso
 * a **todas** las familias que quedaron invitadas y nunca entraron, y devuelve la
 * dirección del cartel con QR para la puerta del tatami.
 *
 * Lleva `Idempotency-Key` **nueva por pulsación**: manda un correo por familia, y un
 * doble clic no puede mandar dos veces el mismo a ciento veinte casas.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import type { ResultadoDeArranque } from '@/backoffice/tipos';
import { enlaceSeguro } from '@/shared/ui/enlaceSeguro';

type Pendiente = 'suspender' | 'activar' | 'arrancar' | null;

export function AccionesDelClub({
  clubId,
  nombre,
  estado,
}: {
  clubId: string;
  nombre: string;
  estado: string;
}) {
  const cache = useQueryClient();
  const [pendiente, setPendiente] = useState<Pendiente>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [arranque, setArranque] = useState<ResultadoDeArranque | null>(null);

  const refrescar = () => {
    void cache.invalidateQueries({ queryKey: ['admin', 'club', clubId] });
    void cache.invalidateQueries({ queryKey: ['admin', 'clubes'] });
    void cache.invalidateQueries({ queryKey: ['admin', 'cronologia', clubId] });
  };

  const cambiarEstado = useMutation({
    mutationFn: async (a: 'suspend' | 'activate') => {
      /*
       * Las dos cadenas enteras y repetidas, en vez de sacar el tronco a un
       * `const`. Es literalmente el ejemplo que `contract:frontend` da de lo que
       * no sabe ver, y hacía que las dos rutas de fase 1 salieran «sin consumir»
       * estando llamadas desde este mismo fichero. 18-09-2026.
       */
      const r =
        a === 'suspend'
          ? await api.v1.admin.academies({ id: clubId }).suspend.post()
          : await api.v1.admin.academies({ id: clubId }).activate.post();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setPendiente(null);
      refrescar();
    },
    onError: (e) => {
      setPendiente(null);
      setProblema(aProblema(e));
    },
  });

  const arrancar = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin.academies({ id: clubId }).onboarding.post(
        {},
        // Clave nueva por pulsación: manda un correo por familia.
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as ResultadoDeArranque;
    },
    onSuccess: (r) => {
      setPendiente(null);
      setArranque(r);
      refrescar();
    },
    onError: (e) => {
      setPendiente(null);
      setProblema(aProblema(e));
    },
  });

  const trabajando = cambiarEstado.isPending || arrancar.isPending;

  return (
    <>
      <h2 className="seccion">Qué se puede hacer</h2>

      {problema && <PanelError problema={problema} />}

      {/* ── El resultado del arranque, que es lo que hay que leer ──────────── */}
      {arranque && (
        <div className="aviso" role="status">
          <p>
            <strong>
              {arranque.guardians_invited === 0
                ? 'No quedaba ninguna familia por avisar'
                : arranque.guardians_invited === 1
                  ? 'Avisada una familia'
                  : `Avisadas ${arranque.guardians_invited} familias`}
            </strong>
            {/*
              Concordancia, y es la tercera vez esta semana que se cuela la misma:
              «1 ya estaban dentro». Interpolar un contador delante de un verbo es
              justo donde pasa, igual que en «1 recibos emitidos» de la previsión de
              caja. Se escribe entera cada rama y se acaba el problema.
            */}
            {arranque.guardians_already_in === 1
              ? ' · una ya estaba dentro y no se le ha escrito.'
              : arranque.guardians_already_in > 1
                ? ` · ${arranque.guardians_already_in} familias ya estaban dentro y no se les ha escrito.`
                : ''}
          </p>

          {arranque.already_onboarded && (
            /*
             * No es un fallo y no se pinta como tal: `onboarded_at` se pone una vez
             * y relanzarlo vuelve a avisar solo a quien sigue sin entrar. Es
             * exactamente lo que se quiere dos semanas después del alta.
             */
            <p className="tenue">
              El arranque ya se había lanzado el {fecha(arranque.onboarded_at)}.
              Relanzarlo es seguro: la fecha del alta es una, y el aviso solo
              sale para quien sigue sin entrar.
            </p>
          )}

          <p>
            El cartel con QR para la puerta del tatami:{' '}
            <a
              href={enlaceSeguro(arranque.poster_url)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {arranque.poster_url}
            </a>
          </p>

          <div className="aviso-pie">
            <span className="tenue">
              {/* Lo que esta ruta todavía no hace, dicho aquí y no en una nota de versión. */}
              La importación del Excel del club no entra aquí todavía: hace
              falta por dónde subir el fichero (§S.3).
            </span>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setArranque(null)}
            >
              Vale
            </button>
          </div>
        </div>
      )}

      {/* ── La confirmación, con la explicación dentro ─────────────────────── */}
      {pendiente === 'suspender' && (
        <div className="aviso aviso-grave" role="alert">
          <p>
            <strong>Suspender no apaga el club.</strong> «{nombre}» pasa a solo
            lectura: las familias siguen viendo el historial de sus hijos y
            nadie puede escribir nada —ni pasar lista, ni lanzar la remesa—.
          </p>
          <p className="tenue">
            Es a propósito. Un club que se retrasa un mes en pagarnos no puede
            dejar a 120 familias sin sus datos: eso cuesta más que la cuota.
          </p>
          <div className="hueco-acciones">
            <button
              type="button"
              disabled={trabajando}
              onClick={() => cambiarEstado.mutate('suspend')}
            >
              {trabajando ? 'Suspendiendo…' : 'Sí, suspenderlo'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={trabajando}
              onClick={() => setPendiente(null)}
            >
              No
            </button>
          </div>
        </div>
      )}

      {pendiente === 'activar' && (
        <div className="aviso" role="alert">
          <p>
            <strong>«{nombre}» vuelve a poder escribir.</strong> Pasar lista,
            cobrar y avisar a las familias funcionan otra vez desde este
            momento.
          </p>
          <div className="hueco-acciones">
            <button
              type="button"
              disabled={trabajando}
              onClick={() => cambiarEstado.mutate('activate')}
            >
              {trabajando ? 'Reactivando…' : 'Sí, reactivarlo'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={trabajando}
              onClick={() => setPendiente(null)}
            >
              No
            </button>
          </div>
        </div>
      )}

      {pendiente === 'arrancar' && (
        <div className="aviso" role="alert">
          <p>
            <strong>
              Esto manda un correo a cada familia que sigue sin entrar.
            </strong>{' '}
            No es un aviso interno: sale de verdad, y a la vez.
          </p>
          <p className="tenue">
            Es lo que rompe el arranque en frío: si el club no usa la app las
            familias no la instalan, y al revés. Se lanza el día del alta, con
            el cartel del QR ya colgado.
          </p>
          <div className="hueco-acciones">
            <button
              type="button"
              disabled={trabajando}
              onClick={() => arrancar.mutate()}
            >
              {trabajando ? 'Avisando…' : 'Sí, lanzar el arranque'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={trabajando}
              onClick={() => setPendiente(null)}
            >
              No
            </button>
          </div>
        </div>
      )}

      {/* ── Los botones ────────────────────────────────────────────────────── */}
      {pendiente === null && (
        <div className="hueco-acciones">
          {estado === 'suspended' ? (
            <button
              type="button"
              onClick={() => {
                setProblema(null);
                setPendiente('activar');
              }}
            >
              Reactivar el club
            </button>
          ) : (
            <button
              type="button"
              className="btn-plano"
              onClick={() => {
                setProblema(null);
                setPendiente('suspender');
              }}
            >
              Suspender por impago
            </button>
          )}

          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setProblema(null);
              setPendiente('arrancar');
            }}
          >
            Lanzar el arranque
          </button>
        </div>
      )}
    </>
  );
}
